import { abyssColors } from '@/theme/colors';
import { SxProps, styled as muiStyled, IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import { styled } from '@mui/material/styles';

// ── History Timeline ──

export const TimelineRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLast',
})<{ isLast?: boolean }>(({ isLast }) => ({
  display: 'flex',
  position: 'relative',
  minHeight: isLast ? 'auto' : 100,
  paddingTop: 4,
}));

export const TimelineTrack = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginRight: 16,
  position: 'relative',
  paddingTop: 4,
}));

export const TimelineDot = styled(Box)(() => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: abyssColors.primary[500],
  flexShrink: 0,
  zIndex: 1,
}));

export const TimelineLine = styled(Box)(() => ({
  position: 'absolute',
  top: 14,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 1,
  bottom: -24,
  backgroundColor: abyssColors.primary[200],
}));

export const TimelineContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLast',
})<{ isLast?: boolean }>(({ isLast }) => ({
  flexGrow: 1,
  paddingBottom: isLast ? 0 : 24,
}));

// ── History Entry Cards ──

export const EntryCard = styled(Box)(() => ({
  padding: 16,
  border: `1px solid ${abyssColors.primary[100]}`,
  borderRadius: 4,
  backgroundColor: abyssColors.neutral[50],
}));

export const EntryTimestamp = styled(Typography)(() => ({
  display: 'flex',
  alignItems: 'center',
  color: abyssColors.primary[400],
  fontSize: '12px',
  marginBottom: 4,
  gap: 16,
}));

export const EntryDescription = styled(Typography)(() => ({
  color: abyssColors.primary[500],
  lineHeight: 1.6,
  fontSize: '12px',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
}));

// ── Hours Update Accordion ──

export const HoursAccordion = styled(Accordion)(() => ({
  boxShadow: 'none',
  border: `1px solid ${abyssColors.primary[50]}`,
  borderRadius: '4px !important',
  backgroundColor: abyssColors.primary[50],
  '&:before': { display: 'none' },
  '&.Mui-expanded': { margin: 0 },
}));

export const HoursAccordionSummary = styled(AccordionSummary)(() => ({
  minHeight: 48,
  backgroundColor: abyssColors.primary[50],
  '& .MuiAccordionSummary-content': { margin: '12px 0' },
}));

export const HoursAccordionDetails = styled(AccordionDetails)(() => ({
  paddingTop: 0,
  paddingLeft: 16,
  paddingRight: 16,
  paddingBottom: 16,
  backgroundColor: abyssColors.primary[50],
}));

// ── Section Table ──

export const SectionHeaderRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginBottom: 8,
}));

export const SectionTitle = styled(Typography)(() => ({
  fontWeight: 500,
  color: abyssColors.primary[500],
}));

export const SectionLabel = styled(Typography)(() => ({
  fontWeight: 400,
  fontSize: '12px',
  color: abyssColors.primary[500],
}));

export const SectionValueStrikethrough = styled(Typography)(() => ({
  color: abyssColors.primary[300],
  textDecoration: 'line-through',
}));

export const SectionValueUpdated = styled(Typography)(() => ({
  fontWeight: 500,
  color: abyssColors.primary[500],
}));

export const SectionTableContainer = styled(TableContainer)(() => ({
  borderRadius: 4,
}));

export const SectionBodyCell = styled(TableCell)(() => ({
  border: `1px solid ${abyssColors.primary[100]}`,
  backgroundColor: abyssColors.neutral[50],
  color: abyssColors.primary[500],
}));

export const SectionHeaderCell = styled(TableCell)(() => ({
  color: abyssColors.primary[400],
  fontWeight: 500,
}));

export const ChangeValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'changeDirection',
})<{ changeDirection: 'positive' | 'negative' | 'neutral' }>(({ changeDirection }) => ({
  fontWeight: 500,
  color:
    changeDirection === 'positive'
      ? abyssColors.success[600]
      : changeDirection === 'negative'
        ? abyssColors.error[600]
        : abyssColors.neutral[500],
}));

export const accordionStyles = {
  boxShadow: 'none',
  '&:before': { display: 'none' },
};

export const getAccordionSummaryStyles = (expanded: boolean): SxProps => ({
  backgroundColor: abyssColors.primary[50],
  border: `1px solid ${abyssColors.primary[100]}`,
  borderRadius: expanded ? '4px 4px 0 0' : 1,
  minHeight: 48,
  flexDirection: 'row-reverse',
  justifyContent: 'flex-end',
  '& .MuiAccordionSummary-content': { margin: '0.75rem 0', marginLeft: 1.5 },
});

export const accordianDetailsStyles: SxProps = {
  p: 1,
  border: `1px solid ${abyssColors.primary[100]}`,
  borderTop: 'none',
};

export const headerCellStyles: SxProps = {
  color: abyssColors.primary[400],
  fontSize: '1.3rem',
  fontWeight: 400,
  pt: 1,
  pb: 0.75,
  pl: 0.5,
};

export const bodyCellStyles: SxProps = {
  py: 0,
  px: 2,
};

export const totalRowCellStyles: SxProps = {
  py: 0.75,
  px: 2,
  fontWeight: 600,
  borderTop: `2px solid ${abyssColors.primary[200]}`,
  backgroundColor: abyssColors.primary[50],
};

export const inputCellStyles: SxProps = {
  border: '1px solid #7E9BBC !important',
};
export const textFieldStyles: SxProps = {
  '& .MuiOutlinedInput-root': { border: 'none' },
  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
  '& .MuiOutlinedInput-input': { border: 'none' },
  '& .MuiOutlinedInput-input:focus': { border: 'none' },
};

export const tooltipStyles = {
  tooltip: {
    sx: { backgroundColor: abyssColors.primary[500] },
  },
  arrow: {
    sx: { color: abyssColors.primary[500] },
  },
  popper: {
    sx: {
      '& .MuiTooltip-tooltip': {
        fontSize: '1.3rem',
        p: 1,
      },
    },
  },
};

export const StyledRemoveButton = muiStyled(IconButton)(() => ({
  position: 'absolute',
  top: -6,
  right: -6,
  zIndex: 2,

  width: 20,
  height: 20,
  padding: 0,

  color: '#fff',
  backgroundColor: abyssColors.error[600],
  borderRadius: '50%',

  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
  '& svg': {
    fontSize: 12,
  },

  '&:hover': {
    backgroundColor: abyssColors.error[700],
  },

  '&:active': {
    backgroundColor: abyssColors.error[800],
  },
}));

export const shiftSelectorContainerStyles: SxProps = {
  display: 'flex',
  backgroundColor: abyssColors.primary[100],
  borderRadius: 1,
  padding: 0.5,
  gap: 0.5,
};

export const getShiftButtonStyles = (isSelected: boolean): SxProps => ({
  minWidth: 50,
  height: 25,
  borderRadius: 0.75,
  textTransform: 'none',
  fontSize: '1.2rem',
  fontWeight: 600,
  backgroundColor: isSelected ? abyssColors.primary[500] : 'transparent',
  color: isSelected ? abyssColors.neutral[50] : abyssColors.primary[300],
  '&:hover': {
    backgroundColor: isSelected ? abyssColors.primary[500] : abyssColors.primary[100],
  },
  boxShadow: 'none',
  px: 2,
});

export const shiftChipStyles: SxProps = {
  height: 20,
  fontSize: '1.2rem',
  fontWeight: 500,
  backgroundColor: abyssColors.primary[100],
  color: abyssColors.primary[600],
  border: '1px solid',
  borderColor: abyssColors.primary[200],
  borderRadius: '1rem',
  '& .MuiChip-label': {
    px: 1,
  },
};
